import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Paper from "Components/UI/Paper";
import {
  Link,
  Print,
  Shortcut,
  Receipt,
  KeyboardReturn,
} from "@mui/icons-material";
import QuickActionButton from "Components/Invoice/QuickActionButton";
import ArticlesTable from "../../components/ArticlesTable";
import dayjs from "dayjs";

import { getSystemStatusLabelById, getSubStatusLabelById } from "utils/labels";
import { getSystemStatusColorById, getSubStatusColorById } from "utils/colors";

//MUI
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Box, IconButton, Skeleton, Tooltip } from "@mui/material";
import Chip from "@mui/material/Chip";
import ChatIcon from "@mui/icons-material/Chat";

import { DEFAULT_SERVICE } from "utils/options/DEFAULT_SERVICE";
import InvoiceField from "Components/Invoice/InvoiceField";
import PrintModal from "./PrintModal";
import NextEventModal from "./NextEventModal";
import DueDateModal from "./DueDateModal";
import DirectPaymentModal from "./DirectPaymentModal";
import DeductionModal from "./DeductionModal";

import { EVENT_OPTIONS } from "utils/options/EVENT_OPTIONS";
import StatusModal from "./StatusModal";
import RegressModal from "./RegressModal";
import { formatPrice } from "utils/priceformatter";
import { useAuth } from "Hooks/useAuth";
import InterestStopDateModal from "./InterestStopDateModal";

export function InvoiceView({
  latestManualLog,
  setSelectedLogs,
  invoice,
  setInvoice,
  printedInvoices,
  fetchPrintedInvoices,
  loading,
  isClientView,
}) {
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [nextEventModalOpen, setNextEventModalOpen] = useState(false);
  const [interestStopDateModalOpen, setInterestStopDateModalOpen] =
    useState(false);
  const [dueDateModalOpen, setDueDateModalOpen] = useState(false);
  const [directPaymentModalOpen, setDirectPaymentModalOpen] = useState(false);
  const [deductionModalOpen, setDeductionModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [regressModalOpen, setRegressModalOpen] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [hasUpcomingEvent, setHasUpcomingEvent] = useState(false);
  const [isOverdue, setIsOverDue] = useState(false);

  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setIsOverDue(false);

    const getUpcomingEvents = (invoice) => {
      setIsOverDue(false);

      const now = dayjs();

      const eventsWithDates = EVENT_OPTIONS.map((eventOption) => {
        let date = null;
        switch (eventOption.value) {
          case "REMINDER_1":
            date = invoice.Pm1WaitCreateDate
              ? dayjs(invoice.Pm1WaitCreateDate)
              : null;
            break;
          case "REMINDER_2":
            date = invoice.Pm2WaitCreateDate
              ? dayjs(invoice.Pm2WaitCreateDate)
              : null;
            break;
          case "COLLECTION":
            date = invoice.CollectionWaitCreateDate
              ? dayjs(invoice.CollectionWaitCreateDate)
              : null;
            break;
          default:
            date = null;
        }
        return { ...eventOption, date };
      });

      let upcomingEvents = eventsWithDates;

      if (upcomingEvents.length > 1) {
        upcomingEvents = upcomingEvents
          .filter(({ date }) => date && date.isAfter(now, "day"))
          .sort((a, b) => a.date.diff(b.date));
      }

      if (upcomingEvents.length === 0) {
        const pastEvents = eventsWithDates
          .filter(({ date }) => date && date.isBefore(now))
          .sort((a, b) => b.date.diff(a.date));

        if (pastEvents.length > 0) {
          upcomingEvents = [pastEvents[0]];
        }
      }

      return upcomingEvents;
    };

    if (invoice) {
      const events = getUpcomingEvents(invoice);
      const now = dayjs();

      setUpcomingEvents(events);
      if (
        dayjs(invoice?.CalcFfdDate ?? invoice?.OriginalFfdDate).isBefore(
          now,
          "day"
        )
      )
        setIsOverDue(true);
      setHasUpcomingEvent(
        events.length > 0 &&
          (events[0].date.isSame(dayjs(), "day") ||
            events[0].date.isAfter(dayjs(), "day"))
      );
    }
  }, [invoice]);

  const setInvoicePayment = (newInvoice) => {
    setInvoice(newInvoice);
  };

  function getServiceName(value) {
    const service = DEFAULT_SERVICE.find((service) => service.Value === value);
    return service ? service.Name : null;
  }

  const toggleNextEventModalOpen = () => {
    setNextEventModalOpen(!nextEventModalOpen);
  };

  const toggleInterestStopDateModalOpen = () => {
    setInterestStopDateModalOpen(!interestStopDateModalOpen);
  };

  const toggleDueDateModalOpen = () => {
    setDueDateModalOpen(!dueDateModalOpen);
  };

  const toggleDirectPaymentModalOpen = () => {
    setDirectPaymentModalOpen(!directPaymentModalOpen);
  };

  const toggleDeductionModalOpen = () => {
    setDeductionModalOpen(!deductionModalOpen);
  };

  const toggleStatusModalOpen = () => {
    setStatusModalOpen(!statusModalOpen);
  };

  const toggleRegressModalOpen = () => {
    setRegressModalOpen(!regressModalOpen);
  };

  const getLatestPrintDate = useMemo(() => {
    if (!printedInvoices || printedInvoices.length === 0) return "";

    const filteredPrintedInvoices = printedInvoices.filter(
      (invoice) => invoice.FileId > 0
    );

    if (filteredPrintedInvoices.length === 0) return "";

    const latestPrintedInvoice = filteredPrintedInvoices.reduce(
      (highest, current) => {
        return current.fileId > highest.fileId ? current : highest;
      }
    );

    return new Date(latestPrintedInvoice.CreateTime).toLocaleDateString("SV");
  }, [printedInvoices]);

  const containerStyle = {
    px: 10,
    maxWidth: "1300px",
    width: "100%",
    height: "100%",
    borderRadius: "10px",
    boxShadow:
      "0px 5px 6px -3px rgba(0,0,0,0.2), 0px 9px 12px 1px rgba(0,0,0,0.14)",
  };

  return (
    <>
      <Paper sx={containerStyle}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box mb={3}>
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"center"}
              gap={3}
            >
              {!invoice ? (
                <Skeleton height={45} width={180} />
              ) : (
                <Typography
                  color={"black"}
                  variant="h5"
                  fontWeight={"bold"}
                  align="left"
                >
                  {invoice?.Type === "Credit" ? "Kreditfaktura" : "Faktura"}
                  {" - "}
                  {invoice?.ExternalInvoiceNo}
                </Typography>
              )}
              <Box display={"flex"} flexDirection={"row"} gap={1}>
                {!invoice ? (
                  <Skeleton width={90} height={40} />
                ) : (
                  <Chip
                    label={getSystemStatusLabelById(invoice?.SystemStatus)}
                    color={getSystemStatusColorById(invoice?.SystemStatus)}
                    onClick={toggleStatusModalOpen}
                    sx={{
                      height: 24,
                      width: 80,
                      "&:hover": { opacity: 0.8, cursor: "pointer" },
                    }}
                  />
                )}
                {!invoice ? (
                  <Skeleton width={90} height={40} />
                ) : invoice?.SystemStatus !== "2" &&
                  invoice.Type !== "Credit" ? (
                  <Chip
                    label={getSubStatusLabelById(invoice?.Substatus)}
                    color={getSubStatusColorById(invoice?.Substatus)}
                    sx={{
                      height: 24,
                      width: "auto",
                      minWidth: 80,
                    }}
                  />
                ) : null}
              </Box>

              <Box>
                {latestManualLog ? (
                  <Tooltip title={latestManualLog.Message}>
                    <ChatIcon
                      sx={{
                        color: "orange",
                        cursor: "pointer",
                      }}
                      onClick={() => setSelectedLogs(true)}
                    />
                  </Tooltip>
                ) : null}
              </Box>
            </Box>
            <Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
              <IconButton
                sx={{ marginLeft: -1.3 }}
                onClick={() =>
                  isClientView
                    ? navigate(`/mypages`)
                    : navigate(`/clients/${invoice.ClientId}`)
                }
              >
                <Link color="primary" />
              </IconButton>

              {!invoice ? (
                <Skeleton width={160} height={30} />
              ) : (
                <Typography align="left">
                  {invoice?.ClientNumber}
                  {" - "}
                  {invoice?.ClientName}
                </Typography>
              )}
            </Box>
          </Box>
          {!isClientView ? (
            <Box
              display="flex"
              justifyContent={"flex-end"}
              alignItems={"flex-start"}
              gap={5}
            >
              <QuickActionButton
                title={"SKRIV UT"}
                onClick={() => setPrintModalOpen(!printModalOpen)}
                icon={<Print />}
              />
              <QuickActionButton
                title={"DIREKTBETALNING"}
                icon={<Shortcut />}
                onClick={toggleDirectPaymentModalOpen}
              />
              <QuickActionButton
                title={"AVSKRIVNING"}
                icon={<Receipt />}
                onClick={toggleDeductionModalOpen}
              />
              <QuickActionButton
                title={"REGRESS"}
                icon={<KeyboardReturn />}
                onClick={toggleRegressModalOpen}
                disabled={invoice?.Regress !== "1"}
              />
            </Box>
          ) : null}
        </Box>
        <Box display="flex" flexDirection={"column"}>
          <Typography
            color={"black"}
            fontSize={14}
            textAlign="left"
            fontWeight={"bold"}
            align="left"
            borderBottom={"2px solid #f3f3f3"}
            paddingBottom={"0.25rem"}
            paddingTop={"2rem"}
            marginBottom={"1rem"}
          >
            Slutkundsinformation
          </Typography>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              gap: "2rem",
            }}
          >
            <InvoiceField
              loading={loading}
              label={"Externt Kundnummer"}
              value={invoice?.EndCustomerExternalCustomerNo}
              md={0}
            />
            <InvoiceField
              loading={loading}
              label={"Slutkund"}
              value={invoice?.EndCustomerName}
              onNavigate={
                !isClientView
                  ? () => navigate(`/endcustomers/${invoice?.EndCustomerId}`)
                  : null
              }
              md={0}
            />
            <InvoiceField
              loading={loading}
              label={"Org. nr"}
              value={invoice?.EndCustomerOrgNo}
            />
            <InvoiceField
              loading={loading}
              label={"Adress"}
              value={invoice?.EndCustomerAddress}
            />
            <InvoiceField
              loading={loading}
              label={"Adress 2"}
              value={invoice?.EndCustomerAddress2}
            />
            <InvoiceField
              loading={loading}
              label={"Post. nr"}
              value={invoice?.EndCustomerPostalCode}
            />
            <InvoiceField
              loading={loading}
              label={"Postort"}
              value={invoice?.EndCustomerCity}
            />
          </div>
          <Typography
            color={"black"}
            fontSize={14}
            textAlign="left"
            fontWeight={"bold"}
            align="left"
            borderBottom={"2px solid #f3f3f3"}
            paddingBottom={"0.25rem"}
            paddingTop={"4rem"}
            marginBottom={"1rem"}
          >
            Fakturainformation
          </Typography>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              gap: "2rem",
            }}
          >
            <InvoiceField
              loading={loading}
              label={"Belopp"}
              value={`${formatPrice(invoice?.Price)}`}
            />
            <InvoiceField
              loading={loading}
              label={"Återstående Belopp"}
              value={`${formatPrice(invoice?.RemainingAmount)}`}
            />
            <InvoiceField
              loading={loading}
              label={"Fakturadatum"}
              value={invoice?.InvoiceDate}
            />
            <InvoiceField
              loading={loading}
              label={`FFD Datum`}
              value={`${invoice?.CalcFfdDate ?? invoice?.OriginalFfdDate} ${
                isOverdue ? "(Förfallen)" : ""
              }`}
              onEdit={isClientView ? null : toggleDueDateModalOpen}
            />
            <InvoiceField
              loading={loading}
              label={"Fakturatjänst"}
              value={getServiceName(invoice?.Service)}
            />
            <InvoiceField
              loading={loading}
              label={"Regressdatum"}
              value={invoice?.RegressDate}
            />
            <InvoiceField
              loading={loading}
              label={"Återfört"}
              value={formatPrice(invoice?.AdjustedAmount)}
            />
            <InvoiceField
              loading={loading}
              label={"Buntnummer"}
              value={invoice?.BundleId}
            />
            <InvoiceField
              loading={loading}
              label={"Utskriven"}
              value={getLatestPrintDate}
            />
            <InvoiceField
              loading={loading}
              label={"Migrerad"}
              value={invoice?.Migrated === 1 ? "Ja" : "Nej"}
            />
            <InvoiceField
              loading={loading}
              label={"Nästa Händelse"}
              value={`${upcomingEvents[0]?.label || "Ingen händelse"} ${
                !hasUpcomingEvent ? "(Passerad)" : ""
              }`}
              onEdit={!isClientView ? toggleNextEventModalOpen : null}
            />
            <InvoiceField
              loading={loading}
              label={"Vår Referens"}
              value={invoice?.OurReference}
            />
            <InvoiceField
              loading={loading}
              label={"Er Referens"}
              value={invoice?.ExternalReference}
            />
            <InvoiceField
              loading={loading}
              label={"Ordernummer"}
              value={null}
            />
            {isAdmin && (
              <InvoiceField
                loading={loading}
                label={"Räntestoppdatum"}
                value={invoice?.InterestStopDate}
                onEdit={
                  !isClientView && invoice?.InterestStopDate
                    ? toggleInterestStopDateModalOpen
                    : null
                }
              />
            )}
          </div>
        </Box>

        {!isClientView ? (
          <>
            <PrintModal
              invoice={invoice}
              setInvoice={setInvoice}
              fetchPrintedInvoices={fetchPrintedInvoices}
              open={printModalOpen}
              onClose={() => setPrintModalOpen(false)}
            />
            <NextEventModal
              open={nextEventModalOpen}
              setInvoice={setInvoice}
              onClose={() => {
                setNextEventModalOpen(false);
              }}
              invoice={invoice}
              upcomingEvents={upcomingEvents}
            />
            <DueDateModal
              open={dueDateModalOpen}
              setInvoice={setInvoicePayment}
              onClose={() => setDueDateModalOpen(false)}
              invoice={invoice}
            />
            <DirectPaymentModal
              open={directPaymentModalOpen}
              setInvoice={setInvoicePayment}
              onClose={() => setDirectPaymentModalOpen(false)}
              invoice={invoice}
              service={getServiceName(invoice?.Service)}
            />
            <DeductionModal
              open={deductionModalOpen}
              setInvoice={setInvoicePayment}
              onClose={() => setDeductionModalOpen(false)}
              invoice={invoice}
              service={getServiceName(invoice?.Service)}
            />
            <StatusModal
              open={statusModalOpen}
              onClose={() => setStatusModalOpen(false)}
              invoice={invoice}
              setInvoice={setInvoice}
            />
            <RegressModal
              open={regressModalOpen}
              onClose={() => setRegressModalOpen(false)}
              invoice={invoice}
              setInvoice={setInvoice}
            />
            <InterestStopDateModal
              open={interestStopDateModalOpen}
              onClose={() => {
                setInterestStopDateModalOpen(false);
              }}
              setInvoice={setInvoice}
              invoice={invoice}
            />
          </>
        ) : null}

        <Box sx={{ px: 0 }}>
          {loading ? (
            <Skeleton height={300} />
          ) : invoice?.Lines ? (
            <ArticlesTable rows={JSON.parse(invoice?.Lines)} />
          ) : null}
        </Box>

        {/*<Box display="flex" flexDirection={"column"}>
          <Typography
            color={"black"}
            fontSize={14}
            textAlign="left"
            fontWeight={"bold"}
            align="left"
            borderBottom={"2px solid #f3f3f3"}
            paddingBottom={"0.25rem"}
            paddingTop={"2rem"}
            marginBottom={"1rem"}
          >
            Avgifter
          </Typography>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr",
              gap: "2rem",
            }}
          >
            <InvoiceField
              loading={loading}
              label={"Administration"}
              value={invoice?.AdministrationFee}
            />
            <InvoiceField
              loading={loading}
              label={"Frakt"}
              value={invoice?.Freight}
            />
            <InvoiceField
              loading={loading}
              label={"Fraktmoms"}
              value={invoice?.FreightVAT}
            />
          </div>
        </Box>*/}
      </Paper>
    </>
  );
}
